import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface LoadingStates {
  dashboard: boolean;
  jobSearch: boolean;
  analytics: boolean;
  insights: boolean;
  applications: boolean;
  resumes: boolean;
  profile: boolean;
  settings: boolean;
  subscribe: boolean;
  upgrade: boolean;
}

interface LoadingContextType {
  loadingStates: LoadingStates;
  setLoading: (section: keyof LoadingStates, isLoading: boolean) => void;
  setMultipleLoading: (states: Partial<LoadingStates>) => void;
  clearAllLoading: () => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

interface LoadingProviderProps {
  children: ReactNode;
}

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [loadingStates, setLoadingStates] = useState<LoadingStates>({
    dashboard: false,
    jobSearch: false,
    analytics: false,
    insights: false,
    applications: false,
    resumes: false,
    profile: false,
    settings: false,
    subscribe: false,
    upgrade: false,
  });

  const setLoading = useCallback((section: keyof LoadingStates, isLoading: boolean) => {
    setLoadingStates(prev => ({
      ...prev,
      [section]: isLoading
    }));
  }, []);

  const setMultipleLoading = useCallback((states: Partial<LoadingStates>) => {
    setLoadingStates(prev => ({
      ...prev,
      ...states
    }));
  }, []);

  const clearAllLoading = useCallback(() => {
    setLoadingStates({
      dashboard: false,
      jobSearch: false,
      analytics: false,
      insights: false,
      applications: false,
      resumes: false,
      profile: false,
      settings: false,
      subscribe: false,
      upgrade: false,
    });
  }, []);

  return (
    <LoadingContext.Provider value={{
      loadingStates,
      setLoading,
      setMultipleLoading,
      clearAllLoading
    }}>
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

export default LoadingContext;
